/**
 * Reading the session-break flags in `calendar.data.expandedItems` from month
 * view, where "no flag at all" has to mean showing.
 *
 * Session breaks are expanded by default. Day/week view get that for free:
 * `expandedItems` is wiped on every date/calendar switch, and DraggableContainer
 * re-seeds each break to `true` when the list mounts. Month view never mounts
 * that list, so it reads a map with no entry for most breaks — and `false` is
 * also what an entry a coach explicitly collapsed holds. Only the explicit
 * `false` means collapsed, which is the distinction `setSessionBreakCollapsed`
 * was written to preserve.
 */

/**
 * Whether a session break is showing the items beneath it.
 *
 * @param {?Object} expandedItems `calendar.data.expandedItems`
 * @param {string|number} assignedId the session break's id
 * @returns {boolean} false only for a break the coach collapsed
 */
export const isSessionBreakShowingItems = (expandedItems, assignedId) => (
  expandedItems?.[assignedId] !== false
);

/**
 * The same flags as an explicit map, for the drop helpers in
 * `dragSessionBreakHelpers` — they read a missing key as collapsed, so they
 * need every break in play spelled out or a drag would redirect around
 * "collapsed" groups the coach can plainly see.
 *
 * @param {?Object} expandedItems `calendar.data.expandedItems`
 * @param {...?Array<Object>} itemArrays the days taking part in the drag
 * @returns {Object} assignedId -> whether that break is showing its items
 */
export const buildMonthViewExpandedMap = (expandedItems, ...itemArrays) => {
  const map = {};
  itemArrays.forEach((items) => {
    if (!items) return;
    items.forEach((item) => {
      if (item?.type === 'session_break' && item.assignedId !== undefined) {
        map[item.assignedId] = isSessionBreakShowingItems(expandedItems, item.assignedId);
      }
    });
  });
  return map;
};
